// @owners { team: patients }
import { useEffect } from 'react';
import { setPrescriptionPricing } from '~shared/actions/pricing';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { staleTimes } from '~shared/queries/constants';
import { queries } from '~shared/queries/query-keys';
import { useQuery } from '~shared/react-query';
import { useDispatchShared, useSelectorShared } from '~shared/store';

/**
 * Bulk prescription pricing, keyed by prescription_id — the React Query replacement for the
 * deprecated fetchPrescriptionPricing thunk. Runs only when authorized.
 */
export const useGetPrescriptionPricing = () => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const { data, isLoading, isSuccess } = useQuery({
    ...queries.pricing.prescriptions,
    enabled: isAuthorized,
    staleTime: staleTimes.fiveMinutes,
  });

  return { prescriptionPricing: data ?? {}, isLoading, isSuccess };
};

/**
 * Bridge: mirror the React Query prescription-pricing data into the Redux `pricing.prescriptionPricing`
 * slice so the existing pricing selectors (getPrescriptionPricing + dependents) keep working without a
 * Redux networking action. One-directional (RQ → Redux). Mount once at an always-rendered authed root.
 * The bridge is the transitional dual-write step toward strategy A (selectors read RQ directly,
 * slice deleted) — see the redux-thunk-to-react-query-migration skill (.cursor/skills/).
 */
export const usePrescriptionPricingReduxBridge = () => {
  const dispatch = useDispatchShared();
  const { prescriptionPricing, isSuccess } = useGetPrescriptionPricing();

  useEffect(() => {
    if (isSuccess) {
      dispatch(setPrescriptionPricing(prescriptionPricing));
    }
  }, [dispatch, isSuccess, prescriptionPricing]);
};
